import {UIModuleV2} from '@gongxigames/framework/slotV2/baseClasses/uiModuleV2/UiModuleV2';
import {
    CoreDI,
    ElapsedClockCounter,
    FlexBox,
    FlexBoxOptions,
    GameConfig,
    getCurrentTime,
    IEventManagerWithAsync,
    inject,
    LanguageService,
    LAYERS,
    ResizeEventData,
    RichText,
    SlotSettings,
    Signal,
    Store,
} from '@gongxigames/framework/slot';
import {Container, DisplayObject, RenderTexture, Sprite, Graphics} from 'pixi.js';
import {CanBeSignal, getProp} from '@gongxigames/framework/core/fragments/BuilderY';
import {UITextStyles} from '@gongxigames/framework/slotV2/ui/ui-koala/JSX/UIText';
import {$JurisdictionZoneText} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/JurisdictionZone/components/JurisdictionZoneText';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {safeZoneStore} from '@gongxigames/framework/slotV2/services/SafeZone/safeZoneStore';
import {$JurisdictionZoneMain} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/JurisdictionZone/components/JurisdictionZoneMain';
import {$JurisdictionZoneAdditional} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/JurisdictionZone/components/JurisdictionZoneAdditional';
import {specialInfoZoneStore} from '@gongxigames/framework/slotV2/services/SpecialInfoZone/specialInfoZoneStore';
import {COLOR_JURISDICTION_BG} from '@gongxigames/framework/slotV2/ui/ui-koala/CONSTANTS';
import {SpecialInfoStickTo} from '@gongxigames/framework/slotV2/services/SpecialInfoZone/types';

export type JurisdictionComponentData = {
    localeId?: string;
    text?: string;
    value?: CanBeSignal<string | number>;
    fixedPosition?: 'left' | 'right';
    flexPosition?: number;
    order?: number;
    stylePreset: UITextStyles;
    enabled: boolean;
};

type Params = {
    main: Container<DisplayObject>;
    mainParent: Signal<Container<DisplayObject>>;
    additional: Container<DisplayObject>;
    additionalParent: Signal<Container<DisplayObject>>;
    mainPanelFlexBox: FlexBox;
    flexElements: Sprite[];
    additionalElements: RichText[];
    rightBorderElement?: Sprite;
    leftBorderElement?: Sprite;
};

const ELEMENT_OFFSET = 40;
const panelHeight = specialInfoZoneStore.blackPanelHeight;
const zoneProps = {
    panelHeight,
    rows: specialInfoZoneStore.numberOfRows
};

export class JurisdictionZoneModule extends UIModuleV2 {
    public static NAME = 'JurisdictionZoneModule';
    public static layer = LAYERS.KEYPAD;
    // TODO: convert to signals/move to store
    @inject(CoreDI.Settings)
    protected _settings!: SlotSettings;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync;

    protected globalClockSignal = Store.signal('0');
    protected elapsedTimeClockSignal = Store.signal('0');
    protected netPositionSignal = Store.signal('0');
    protected sessionEarningsSignal = Store.signal('0');
    protected sessionBetsSignal = Store.signal('0');
    protected pointsBalanceSignal = Store.signal('0');

    private elapsedClockCounter: ElapsedClockCounter = new ElapsedClockCounter();
    private sessionBets = 0;
    private actionNumberForSessionEarnings = -1;
    private sessionEarnings = 0;
    private sessionNetBalance = 0;

    protected config!: Record<string, JurisdictionComponentData>;

    protected params: Params = {
        main: new Container(),
        mainParent: Store.computed(() => slotStore.stage_isLandscape.value ? this.getMainLandscapeContainer() : this.getMainPortraitContainer()),
        additional: new Container(),
        additionalParent: Store.computed(() => slotStore.stage_isLandscape.value ? this.getAdditionalLandscapeContainer() : this.getAdditionalPortraitContainer()),
        mainPanelFlexBox: new FlexBox({}, slotStore.updateManager.value!),
        flexElements: [],
        additionalElements: [],
    };

    onInit(): void {
        this.config = {
            gameName: {
                text: this.getGameName(),
                stylePreset: 'WATERMARK_LEFT',
                fixedPosition: 'left',
                enabled: true
            },
            pointsBalance: {
                localeId: 'pointsLabel',
                value: this.pointsBalanceSignal,
                stylePreset: 'WATERMARK_LEFT',
                order: 0,
                enabled: !!this._settings.get('isPointsCurrencyEnabled')
            },
            globalClock: {
                localeId: 'elapsedTimeClock',
                value: this.globalClockSignal,
                stylePreset: 'WATERMARK_RIGHT',
                fixedPosition: 'right',
                enabled: this._settings.get('clock')
            },
            netPosition: {
                localeId: 'netPositionBalance',
                value: this.netPositionSignal,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('netPositionEnabled')
            },
            elapsedTime: {
                localeId: 'elapsedTimeClock',
                value: this.elapsedTimeClockSignal,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('elapsedTimeEnabled')
            },
            sessionEarnings: {
                localeId: 'sessionEarnings',
                value: this.sessionEarningsSignal,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('sessionEarningsEnabled')
            },
            sessionBets: {
                localeId: 'sessionBets',
                value: this.sessionBetsSignal,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('sessionBetsEnabled')
            },
            showMaxWinProbability: {
                localeId: 'maxWinProbability',
                value: this._gameConfig.winProbability,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('showMaxWinProbabilityOnUI')
            },
            RTPWatermark: {
                localeId: 'RTPWatermark',
                value: this._gameConfig.RTP,
                stylePreset: 'WATERMARK_LEFT',
                enabled: this._settings.get('showRTPWatermark')
            }
        };

        this.params.main = $JurisdictionZoneMain(zoneProps) as Container;
        this.params.additional = $JurisdictionZoneAdditional(zoneProps) as Container;
    }

    onSetupEvents(): void {
        this.emitter.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.emitter.on('CORE.screen.resize', this.onResize.bind(this));
        this.emitter.on('LogicEvents.POINTS_BALANCE_UPDATED', this.refreshPointsBalance);
    }

    protected onGameStarted(): void {
        this.elapsedClockCounter.init(Date.now(), this._eventManager);
        this.clockTick();
        this.updateSessionBetLabel();
        this.updateSessionEarnings();
        this.updateSessionNetBalance();
        this.refreshPointsBalance();
        this.initPanels(this.params);
        // this.initPanels(this.portrait);
        this.initEffects();

        this.emitter.on('Game.command.refreshBalance', this.onBalanceUpdated.bind(this));
        this.emitter.on('UI.command.updateSessionBet', this.updateSessionBet.bind(this));
    }

    protected onResize(data: ResizeEventData): void {
        this.recalculateFlexElements(this.params);

        if (JurisdictionZoneModule.layer) {
            this.params.mainParent.peek().parentGroup = JurisdictionZoneModule.layer.group;
            this.params.additionalParent.peek().parentGroup = JurisdictionZoneModule.layer.group;
        }
    }

    protected refreshPointsBalance(points = 0): void {
        this.pointsBalanceSignal.value = slotStore.currencyFormatter.peek()?.formatCents(points) || '';
    }

    protected initRowFlexBoxPanel(options: Partial<FlexBoxOptions> = {}): FlexBox {
        return new FlexBox(
            {
                direction: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: {top: 0, bottom: 0, left: 0, right: 0},
                gap: 10,
                wrap: false,
                containerWidth: 0,
                containerHeight: 0,
                ...options
            },
            slotStore.updateManager.value!
        );
    };

    protected initPanels(params: Params): void {
        const data = Object.values(this.config!).filter(value => value.enabled);

        params.mainParent.peek().addChild(params.main);
        params.additionalParent.peek().addChild(params.additional);

        data.filter(({fixedPosition}) => Boolean(fixedPosition))
            .forEach((data) => {
                const sprite = this.getTextSprite(data);

                if (data.fixedPosition === 'left') {
                    sprite.anchor.y = 0;
                    params.leftBorderElement = params.main.addChild(sprite);
                }

                if (data.fixedPosition === 'right') {
                    sprite.anchor.y = 0;
                    params.rightBorderElement = params.main.addChild(sprite);
                }
            });

        const leftBorderWidth = params.leftBorderElement ? params.leftBorderElement.width + ELEMENT_OFFSET : 0;
        const flexParent = new Container();
        const containerWidth = this.geFlexBoxContainerWidth(params);

        params.mainPanelFlexBox = this.initRowFlexBoxPanel({containerWidth});

        flexParent.addChild(params.mainPanelFlexBox);
        params.main.addChild(flexParent);
        flexParent.x = leftBorderWidth;

        this.createMainPanelFlexElements(data, params);
    }

    protected initEffects(): void {
        Store.effect(() => {
            getProp(this.globalClockSignal);
            // getProp(this.elapsedTimeClockSignal);
            getProp(this.netPositionSignal);
            getProp(this.sessionEarningsSignal);
            getProp(this.sessionBetsSignal);
            getProp(this.pointsBalanceSignal);
            this.recalculateFlexElements(this.params);
        }, {signal: this.abortSignal});
    }

    protected getTextSprite(data: JurisdictionComponentData): Sprite {
        const textSprite = $JurisdictionZoneText(data) as RichText;
        const {width, height} = textSprite;
        const renderTexture = RenderTexture.create({ width: width + ELEMENT_OFFSET, height });
        const sprite = new Sprite(renderTexture);

        if (data.order !== undefined) {
            sprite.name = `${data.order}`;
        }
        sprite.addChild(textSprite);
        sprite.anchor.y = 0.5;

        return sprite;
    }

    protected geFlexBoxContainerWidth(params: Params): number {
        const {width} = safeZoneStore.stage_safeZoneState_ui.peek().calcInSafeFader();
        const rightBorderWidth = params.rightBorderElement ? params.rightBorderElement.width + 20 : 0;
        const leftBorderWidth = params.leftBorderElement ? params.leftBorderElement.width + 20 : 0;

        return width - rightBorderWidth - leftBorderWidth;
    }

    protected recalculateFlexElements(params: Params): void {
        const containerWidth = this.geFlexBoxContainerWidth(params);

        params.mainParent.peek().addChild(params.main);
        params.additionalParent.peek().addChild(params.additional);
        params.mainPanelFlexBox.updateOptions({containerWidth});

        this.clearPanelsFromFlexElements(params);
        this.fillMainPanelWithFlexElements(params);

        params.mainPanelFlexBox?.update();
        while (params.mainPanelFlexBox?.width > containerWidth) {
            params.additionalElements.push(params.mainPanelFlexBox.removeChildAt(params.mainPanelFlexBox.children.length - 1) as RichText);
        }

        this.setNumberOfRows(0);
        params.additional.children
            .filter(e => !(e instanceof Graphics))
            .forEach(e => params.additional.removeChild(e));
        this.fillAdditional(params);

        this.emitter.emit('UI.specialZone.relocate');
    }

    protected fillAdditional(params: Params, numberOfRows = 0): void {
        if (params.additionalElements.length === 0) {
            return;
        }
        const {additional, additionalElements} = params;
        const options = this.getFlexBoxOptions();
        const flexBox = this.initRowFlexBoxPanel(options);

        additional.addChild(flexBox);
        additionalElements.forEach((e) => flexBox.addChild(e));

        if (this.checkPosition('top-right')) {
            additionalElements.forEach((e) => this.addGraphics(e, true));

            return;
        }

        if (this.checkPosition('top-left')) {
            additionalElements.forEach((e) => this.addGraphics(e));

            return;
        }

        flexBox.y = specialInfoZoneStore.blackPanelHeight.peek() * numberOfRows;
        numberOfRows++;

        additionalElements.length = 0;
        flexBox.update();
        while (flexBox.width > (options?.containerWidth ?? 0)) {
            additionalElements.push(flexBox.removeChildAt(flexBox.children.length - 1) as RichText);
        }

        this.setNumberOfRows(numberOfRows);

        if (additionalElements.length > 0) {
            this.fillAdditional(params, numberOfRows);
        }
    }

    protected setNumberOfRows(numberOfRows: number): void {
        specialInfoZoneStore.numberOfRows.value = numberOfRows;
    }

    protected addGraphics(sprite: Sprite, anchorX = false): void {
        const {width, height} = sprite.getChildAt(sprite.children.length - 1) as RichText;
        const graphicsWidth = width + ELEMENT_OFFSET;
        let gfx: Graphics;

        if (sprite.children.length === 1) {
            gfx = new Graphics();

            gfx.beginFill(COLOR_JURISDICTION_BG);
            gfx.drawRect(-ELEMENT_OFFSET / 2, -(height + 10) / 2, graphicsWidth, height + 10);
            gfx.endFill();
        } else {
            gfx = sprite.getChildAt(0) as Graphics;
        }

        gfx.width = graphicsWidth;

        sprite.anchor.x = anchorX ? 0.5 : 0;
        sprite.addChildAt(gfx, 0);
    }

    protected resetSprite(sprite: Sprite): void {
        const elem = sprite.getChildAt(0);

        sprite.parent.removeChild(sprite);
        sprite.anchor.set(0, 0.5);

        if (elem instanceof Graphics) {
            elem.parent.removeChild(elem);
        }
    }

    protected checkPosition(positionToCheck: SpecialInfoStickTo): boolean {
        return specialInfoZoneStore.position.peek().includes(positionToCheck);
    }

    protected getFlexBoxOptions(): Partial<FlexBoxOptions> {
        const {width} = safeZoneStore.stage_safeZoneState_ui.peek().calcInSafeFader();
        let options: Partial<FlexBoxOptions> = {containerWidth: width};

        if (this.checkPosition('top-left')) {
            options = {
                ...options,
                direction: 'column',
                gap: 0,
                justifyContent: 'start',
                alignItems: 'start',
            };
        } else if (this.checkPosition('top-right')) {
            options = {
                ...options,
                direction: 'column',
                gap: 0,
                justifyContent: 'end',
                alignItems: 'end',
            };
        }

        return options;
    }

    protected createMainPanelFlexElements(data: JurisdictionComponentData[], params: Params): void {
        const flexElements = data.filter((data) => {
            return data.fixedPosition === undefined;
        });

        flexElements.forEach((data) => {
            const sprite = this.getTextSprite(data);

            params.flexElements.push(params.mainPanelFlexBox.addChild(sprite));
        });
        params.flexElements.sort((a, b) => {
            if (a.name && b.name) {
                return Number(a.name) - Number(b.name);
            }

            if (a.name) {
                return -1;
            }

            if (b.name) {
                return 1;
            }

            return a.width - b.width;
        });
    }

    protected fillMainPanelWithFlexElements(params: Params): void {
        params.flexElements.forEach((element) => {
            params.mainPanelFlexBox.addChild(element);
        });
    }

    protected clearPanelsFromFlexElements(params: Params): void {
        params.flexElements.forEach((e) => this.resetSprite(e));
        params.additionalElements = [];
    }

    protected getMainPortraitContainer(): Container {
        return this.getParentContainer().getChildByName('MainJurisdictionZone_portrait');
    }

    protected getMainLandscapeContainer(): Container {
        return this.getParentContainer().getChildByName('MainJurisdictionZone_landscape');
    }

    protected getAdditionalPortraitContainer(): Container {
        return this.getParentContainer().getChildByName('AdditionalJurisdictionZone_portrait');
    }

    protected getAdditionalLandscapeContainer(): Container {
        return this.getParentContainer().getChildByName('AdditionalJurisdictionZone_landscape');
    }

    // TODO - one logic for this class and WaterMarkModule and GameInfoService
    protected getGameName(): string {

        const localizeGameName = this._languageService.get('gameName');
        const str = localizeGameName === 'undefined' ? this._gameConfig.gameName : localizeGameName;

        return str.toUpperCase();
    }

    protected addSessionEarnings(): void {
        const actionNumber = slotStore.actionNumber.value;

        if (slotStore.logic_nextActionIsSpin.value && this.actionNumberForSessionEarnings !== actionNumber) {
            this.sessionEarnings += slotStore.logic_totalWin.value;
            this.actionNumberForSessionEarnings = actionNumber;
        }
    }

    protected updateSessionEarnings(): void {
        this.addSessionEarnings();
        this.sessionEarningsSignal.value = slotStore.currencyFormatter.value?.formatCents(this.sessionEarnings) || '';
    }

    protected updateSessionNetBalance(): void {
        this.sessionNetBalance = this.sessionEarnings - this.sessionBets;

        this.netPositionSignal.value = slotStore.currencyFormatter.value?.formatCents(this.sessionNetBalance) || '';
    }

    protected onBalanceUpdated(): void {
        this.updateSessionEarnings();
        this.updateSessionNetBalance();
    }

    protected clockTick(): void {
        this.updateClock();
        slotStore.updateManager.value?.addTimer(this.clockTick.bind(this), 1000, true);
    }

    protected updateClock(): void {
        this.globalClockSignal.value = getCurrentTime();

        if (!this.elapsedClockCounter.isPaused) {
            this.elapsedTimeClockSignal.value = this.elapsedClockCounter.getTime();
        }
    }

    protected updateSessionBetLabel(): void {
        this.sessionBetsSignal.value = slotStore.currencyFormatter.value?.formatCents(this.sessionBets) || '';
    }

    protected updateSessionBet(): void {
        if (slotStore.logic_nextAction.value === Action.SPIN) {
            this.sessionBets += slotStore.logic_totalBet.value;
            this.updateSessionBetLabel();
        }
    }
}